import React, { useState } from 'react';
import { formatRelativeTime, formatFullDateTime } from '../../../helpers/timeFormatter';

/**
 * CaseHistoryItem - Individual case entry in the history panel
 *
 * Features:
 * - Checkbox for selection
 * - Patient name with status badge
 * - Relative time display with tooltip
 * - Case ID display with copy functionality
 * - Click to edit, delete button
 * - Staggered entrance animation
 */
const CaseHistoryItem = ({
  caseData,
  selected,
  isEditing = false,
  onSelect,
  onEdit,
  onDelete,
  animationDelay = 0,
}) => {
  const [copied, setCopied] = useState(false);
  const [isHoveringId, setIsHoveringId] = useState(false);

  const handleRowClick = (e) => {
    // Don't trigger edit when clicking checkbox, delete button, or case ID area
    if (
      e.target.closest('.case-history-item__checkbox') ||
      e.target.closest('.case-history-item__delete') ||
      e.target.closest('.case-history-item__case-id-section')
    ) {
      return;
    }
    onEdit?.(caseData);
  };

  const handleCheckboxChange = (e) => {
    e.stopPropagation();
    onSelect?.(caseData.key);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete?.(caseData.key);
  };

  const handleCopyCaseId = async (e) => {
    e.stopPropagation();
    if (caseData.caseId) {
      try {
        await navigator.clipboard.writeText(caseData.caseId);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  // Build class names
  const itemClasses = [
    'case-history-item',
    selected && 'case-history-item--selected',
    isEditing && 'case-history-item--editing',
  ].filter(Boolean).join(' ');

  const caseIdContainerStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    marginLeft: '8px',
    padding: '2px 6px',
    borderRadius: '3px',
    cursor: 'pointer',
    background: isHoveringId ? 'rgba(99, 102, 241, 0.1)' : 'rgba(0,0,0,0.04)',
    transition: 'background 0.15s ease',
  };

  const caseIdTextStyle = {
    fontSize: '0.65rem',
    fontWeight: 500,
    color: '#9ca3af',
    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
    maxWidth: '90px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const copyIconStyle = {
    display: 'flex',
    alignItems: 'center',
    opacity: isHoveringId || copied ? 1 : 0,
    transition: 'opacity 0.15s ease',
    color: copied ? '#22c55e' : '#9ca3af',
    flexShrink: 0,
  };

  return (
    <div
      className={itemClasses}
      onClick={handleRowClick}
      style={{ animationDelay: `${animationDelay}s` }}
      title={`Click to load ${caseData.firstName} ${caseData.lastName}'s data into form`}
    >
      <label className="case-history-item__checkbox">
        <input
          type="checkbox"
          checked={selected}
          onChange={handleCheckboxChange}
        />
        <span className="case-history-item__checkbox-custom">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </span>
      </label>

      <div className="case-history-item__content">
        <div className="case-history-item__name-row">
          <span className="case-history-item__name">
            {caseData.firstName} {caseData.lastName}
          </span>
          {isEditing && (
            <span className="case-history-item__status-badge case-history-item__status-badge--editing">
              Editing
            </span>
          )}
          {!isEditing && caseData.wasTriggered && (
            <span className="case-history-item__status-badge case-history-item__status-badge--triggered">
              Triggered
            </span>
          )}
        </div>
        <div className="case-history-item__meta" style={{ display: 'flex', alignItems: 'center' }}>
          <span
            className="case-history-item__time"
            title={formatFullDateTime(caseData.savedAt)}
          >
            {formatRelativeTime(caseData.savedAt)}
          </span>

          {/* Case ID - inline, hover to copy */}
          {caseData.wasTriggered && caseData.caseId && (
            <div
              className="case-history-item__case-id-section"
              style={caseIdContainerStyle}
              onClick={handleCopyCaseId}
              onMouseEnter={() => setIsHoveringId(true)}
              onMouseLeave={() => setIsHoveringId(false)}
              title={copied ? 'Copied!' : `${caseData.caseId} — Click to copy`}
            >
              <span style={caseIdTextStyle}>
                {caseData.caseId}
              </span>
              <span style={copyIconStyle}>
                {copied ? (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                ) : (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                )}
              </span>
            </div>
          )}
        </div>
      </div>

      <button
        type="button"
        className="case-history-item__delete"
        onClick={handleDelete}
        aria-label={`Delete ${caseData.firstName} ${caseData.lastName}`}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
          <line x1="10" y1="11" x2="10" y2="17" />
          <line x1="14" y1="11" x2="14" y2="17" />
        </svg>
      </button>
    </div>
  );
};

export default CaseHistoryItem;
